/**
 * ─────────────────────────────────────────────────────────────────────────────
 * @module   public/js/security/rolegate.js
 * @class    RoleGate
 * @summary  Tiny RBAC utility powered by Firebase custom-claims.
 *
 * @description
 * Exposes **two async helpers**
 *
 * | helper                 | returns *true* if …                                   |
 * |------------------------|-------------------------------------------------------|
 * | `hasAny('admin', …)`   | the signed-in user holds **any** of the roles         |
 * | `isAtLeast('manager')` | user’s top role ≥ target according to `HIERARCHY`     |
 *
 * Token shapes supported
 * • **Preferred:** `{ roles:{ seller:true, admin:true } }` (set-like object)
 * • Legacy:        `{ roles:['seller','admin'] }` **or** `{ role:'admin' }`
 *
 * **Auto-refresh logic**
 * – Clears its in-memory cache whenever Firebase fires `onIdTokenChanged`.
 * – Otherwise a 5-minute TTL ensures promotions take effect quickly.
 *
 * ## Public API
 * new RoleGate(authService)
 *   – hasAny(...roles): Promise\<boolean>
 *   – isAtLeast(role): Promise\<boolean>
 *   – invalidateCache() → void
 *
 * ## Dependencies
 * – **AuthService** – must expose:
 *     • `waitForLogin(ms)` → Promise\<firebase.User\|null>
 *     • `onIdTokenChanged(cb)` (Firebase wrapper already has it)
 *
 * @author   Auto-Generated
 * @version  1.1.0
 * @since    2025-05-07
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { getDoc } from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import { docWithTenant } from '../utils/firestoreHelpers.js';
import { normalizeTenantRoles } from '../core/tenantRoles.js';

export default class RoleGate {
  /** Low → high authority */
  static HIERARCHY = ['seller', 'manager', 'admin', 'super_admin'];

  /** Cache entries live for this many ms (fallback if token wasn’t refreshed) */
  static CACHE_TTL = 5 * 60 * 1_000; // 5 min

  /** @param {import('../services/authservice.js').default} authService */
  constructor(authService) {
    this.auth = authService;
    this._roles = null; // string[] | null
    this._cacheAt = 0; // epoch ms

    /* Invalidate whenever Firebase sends a new ID-token */
    authService.onIdTokenChanged?.(() => {
      this._roles = null;
    });
  }

  /* ─────────── private ─────────── */

  async #fetchRolesFresh() {
    const user = await this.auth.waitForLogin(5_000).catch(() => null);
    if (!user) return [];

    const { claims } = await user.getIdTokenResult(true); // force refresh

    const tenantId = user.tenantId ?? claims.tenantId;
    const roles = [];

    if (claims?.roles?.super_admin === true || claims?.role === 'super_admin') {
      roles.push('super_admin');
    }

    try {
      if (tenantId) {
        const snap = await getDoc(docWithTenant(tenantId, `Users/${user.uid}`));
        if (snap.exists()) {
          const data = snap.data();
          roles.push(...normalizeTenantRoles(data?.roles, data?.role));
        }
      }
    } catch (err) {
      console.warn('[RoleGate] fallback role fetch failed', err);
    }

    return RoleGate.HIERARCHY.filter((role) => roles.includes(role));
  }

  async #getRoles() {
    const now = Date.now();
    if (this._roles && now - this._cacheAt < RoleGate.CACHE_TTL) {
      return this._roles;
    }
    this._roles = await this.#fetchRolesFresh();
    this._cacheAt = now;
    return this._roles;
  }

  /* ─────────── public ─────────── */

  /** Explicitly clear cached roles (rarely needed) */
  invalidateCache() {
    this._roles = null;
  }

  /** true ⇢ user owns *any* of the roles supplied */
  async hasAny(...roles) {
    const mine = await this.#getRoles();
    return roles.some((r) => mine.includes(r));
  }

  /** true ⇢ user’s highest role ≥ target using `HIERARCHY` order */
  async isAtLeast(role) {
    const targetIdx = RoleGate.HIERARCHY.indexOf(role);
    if (targetIdx === -1) return false;

    const mine = await this.#getRoles();
    const topIdx = Math.max(
      ...mine.map((r) => RoleGate.HIERARCHY.indexOf(r)).filter((i) => i >= 0),
      -1
    );
    return topIdx >= targetIdx;
  }
}
